import { type FormEvent, useCallback, useRef, useState } from 'react';
import { ArtworkGrid } from './ArtworkGrid';
import { useManifest } from '../hooks/useArtwork';
import { loadAdminToken } from '../lib/admin-session';
import {
  getManifest,
  removeLocalPiece,
  slugId,
  type ArtworkManifest,
  type Placement,
} from '../lib/artwork';
import { getRuntimeContent, setRuntimeContent } from '../lib/content-runtime';
import { blobToBase64, prepareImageBlob } from '../lib/image-prepare';
import type { SiteContent } from '../lib/site';
import baseSite from '../content/site.json';

const ALL_PLACEMENTS: { id: Placement; label: string }[] = [
  { id: 'hero', label: 'Hero (home)' },
  { id: 'gallery', label: 'Gallery' },
  { id: 'original', label: 'Originals' },
  { id: 'about', label: 'About' },
  { id: 'charity', label: 'Charities' },
];

function adminToken(): string {
  return loadAdminToken();
}

/** Artwork upload — publishes instantly for all visitors via Worker media + KV. */
export function ArtworkToolsPanel() {
  const manifest = useManifest();
  const inputRef = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [forSale, setForSale] = useState(false);
  const [placements, setPlacements] = useState<Placement[]>(['gallery']);
  const [preview, setPreview] = useState<string | null>(null);
  const [fileBlob, setFileBlob] = useState<Blob | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onFiles = useCallback(
    async (files: FileList | File[]) => {
      const file = Array.from(files).find((f) => f.type.startsWith('image/') || f instanceof Blob);
      if (!file) {
        setStatus('Please choose an image file (JPG, PNG, WebP, or GIF).');
        return;
      }
      try {
        setStatus('Preparing picture…');
        const { blob } = await prepareImageBlob(file, 1600, 0.9);
        setFileBlob(blob);
        setPreview(URL.createObjectURL(blob));
        if (!title.trim() && 'name' in file && typeof file.name === 'string') {
          setTitle(file.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' '));
        }
        setStatus('Picture ready — fill in the details and publish.');
      } catch {
        setStatus('Could not prepare that picture. Try another photo.');
      }
    },
    [title],
  );

  async function onPaste(e: React.ClipboardEvent) {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) await onFiles([file]);
        return;
      }
    }
  }

  function togglePlacement(id: Placement) {
    setPlacements((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!fileBlob || !preview) {
      setStatus('Add a picture first (paste, drop, or choose a file).');
      return;
    }
    if (placements.length === 0) {
      setStatus('Choose at least one place for this piece to appear.');
      return;
    }
    const token = adminToken();
    if (!token) {
      setStatus('Please sign in again.');
      return;
    }

    setBusy(true);
    setStatus('Publishing so everyone can see it…');
    try {
      const full = await prepareImageBlob(fileBlob, 1400, 0.88);
      const thumb = await prepareImageBlob(fileBlob, 720, 0.82);
      const imageFull = await blobToBase64(full.blob);
      const imageThumb = await blobToBase64(thumb.blob);
      const live = getRuntimeContent();
      const res = await fetch('/api/admin/artwork', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'x-site-admin-token': token,
        },
        body: JSON.stringify({
          id: slugId(title || 'artwork').replace(/-\w+$/, ''),
          title: title.trim() || 'Untitled',
          description: description.trim(),
          medium: 'Watercolor on paper',
          placements,
          forSale: forSale || placements.includes('original'),
          price: price ? Number(price) : undefined,
          imageFull,
          imageThumb,
          site: live.site ?? (baseSite as SiteContent),
          artwork: getManifest() as ArtworkManifest,
        }),
      });
      const data = (await res.json()) as {
        ok?: boolean;
        error?: string;
        summary?: string;
        site?: SiteContent;
        artwork?: ArtworkManifest;
        updatedAt?: string;
      };
      if (!res.ok || !data.ok) {
        setStatus(data.error || 'Could not publish. Please try again.');
        return;
      }
      if (data.site && data.artwork) {
        setRuntimeContent({
          site: data.site,
          artwork: data.artwork,
          source: 'live',
          updatedAt: data.updatedAt,
        });
      }
      setStatus(data.summary || 'Live for everyone now.');
      setPreview(null);
      setFileBlob(null);
      setTitle('');
      setDescription('');
      setPrice('');
      setForSale(false);
      setPlacements(['gallery']);
    } catch {
      setStatus('Could not publish. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  }

  const livePieces = manifest.pieces.filter((p) => p.src.startsWith('/api/media/'));
  const localOnly = manifest.pieces.filter((p) => p.local && !p.src.startsWith('/api/media/'));

  return (
    <div className="admin-artwork" onPaste={onPaste}>
      <div className="section-head" style={{ marginBottom: '1rem' }}>
        <h2 style={{ marginBottom: '0.35rem' }}>Add artwork</h2>
        <p style={{ margin: 0 }}>
          Paste, drop, or choose a picture. Permanent gallery pieces should look like the cream studio
          product shots (white frame on soft cream — not phone photos on the wall). When you publish, it
          is live for every visitor right away —
          no waiting on a deploy.
        </p>
      </div>

      <div style={{ display: 'grid', gap: '1.25rem' }}>
        <div
          className={`dropzone${drag ? ' active' : ''}`}
          onDragOver={(e) => {
            e.preventDefault();
            setDrag(true);
          }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDrag(false);
            if (e.dataTransfer.files?.length) void onFiles(e.dataTransfer.files);
          }}
          onClick={() => inputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
          }}
        >
          <strong>Paste, drop, or click to add a picture</strong>
          <span style={{ color: 'var(--ink-soft)' }}>
            Tip: copy an image, click here, then paste (⌘V / Ctrl+V). Works great on phones too.
          </span>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={(e) => {
              if (e.target.files?.length) void onFiles(e.target.files);
            }}
          />
        </div>

        {preview ? (
          <div className="hero-frame" style={{ maxWidth: 360, aspectRatio: '4/3' }}>
            <img src={preview} alt="Preview" />
          </div>
        ) : null}

        <form className="form soft-panel" onSubmit={onSubmit}>
          <label>
            Title
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Morning light" />
          </label>
          <label>
            Short description
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="A few words about the piece"
            />
          </label>
          <div>
            <div style={{ marginBottom: '0.45rem', color: 'var(--ink-soft)', fontSize: '0.92rem' }}>
              Show this piece on
            </div>
            <div className="chip-row">
              {ALL_PLACEMENTS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={`chip${placements.includes(p.id) ? ' on' : ''}`}
                  onClick={() => togglePlacement(p.id)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={forSale || placements.includes('original')}
              onChange={(e) => setForSale(e.target.checked)}
            />
            List for sale
          </label>
          {(forSale || placements.includes('original')) && (
            <label>
              Price (USD)
              <input
                type="number"
                min={0}
                step={1}
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="185"
              />
            </label>
          )}
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? 'Publishing…' : 'Publish live now'}
          </button>
        </form>

        {status ? (
          <div className="notice" role="status">
            {status}
          </div>
        ) : null}
      </div>

      {livePieces.length > 0 ? (
        <div style={{ marginTop: '1.75rem' }}>
          <div className="section-head">
            <h3>Live uploads</h3>
            <p>These pictures are already on the public site for everyone.</p>
          </div>
          <ArtworkGrid pieces={livePieces.slice(0, 12)} />
        </div>
      ) : null}

      {localOnly.length > 0 ? (
        <div style={{ marginTop: '1.75rem' }}>
          <div className="section-head">
            <h3>Only on this device</h3>
            <p>Old browser-only drafts — remove or re-publish to make them live for everyone.</p>
          </div>
          <ul style={{ marginTop: '0.5rem' }}>
            {localOnly.map((p) => (
              <li key={p.id}>
                {p.title}{' '}
                <button type="button" className="chip" onClick={() => removeLocalPiece(p.id)}>
                  Remove
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
